/* global document, fetch, window */
import React, { Component } from "react";
import { StaticMap } from "react-map-gl";
import DeckGL, {
  MapView,
  MapController,
  PathLayer,
  IconLayer
  // ScatterplotLayer
} from "deck.gl";
import AnimationLayer from "./animation-layer";
import PropTypes from "prop-types";
import * as RemoteData from "./remote-data";
import { zipVizData } from "./viz-data";
import {loadJson, mapRange} from "./utils";
import {configs} from "./configs";

// Set your mapbox token here
const { REACT_APP_MapboxAccessToken } = process.env;

const INITIAL_MAP_VIEW_STATE = {
  longitude: 11.4258,
  latitude: 48.7570,
  zoom: 13.3,
  maxZoom: 14.5,
  minZoom: 11.5,
  pitch: 53,
  bearing: 0
};

const GEOMETRY_DATA_CHUNKS = 5;

const MAP_DATA_ROOT = "/map-data";


function makeDataId(scenarioCode, avPercent){
  return `${scenarioCode}/${avPercent}`;
}

const iconLayerMappingBaseOptions ={
  width: 300,
  height: 428,
};

const ABpointsLayer = new IconLayer({
  id: 'icon-layer',
  data: [configs.pointA, configs.pointB],
  pickable: false,
  iconAtlas: 'icons/ab-atlas.png',
  iconMapping: {
    "a": {
        ...iconLayerMappingBaseOptions,
      "x": 0,
      "y": 0,
    },
    "b": {
      ...iconLayerMappingBaseOptions,
      "x": 302,
      "y": 0,
    },
  },
  getIcon: (d) => d.marker,
  sizeScale: 15,
  getPosition: d => d.coordinates.reverse(),
  getSize: d => 10
});


function loadGeometry(chunkn) {
  const url = `${MAP_DATA_ROOT}/geometries${chunkn}.json`;
  return loadJson(url);
}

function loadAttributes(dataId) {
  const url = `${MAP_DATA_ROOT}/${dataId}/attributes.json`;
  return loadJson(url);
}

function center(styles, children) {
  const style = {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ...styles
  };
  return <div style={style}>{children}</div>;
}

function renderError(err) {
  return center(
    {
      background: "rgba(255, 0, 0, 0.2)",
    },
    err
  );
}

function renderOverlay(children){
  return <div style={{position: "absolute", width: "100%", height: "100%", color: "white"}}>{children}</div>
}

function renderLoading() {
  return center({}, "Loading..");
}

function tryMakeLayers(remoteData, time) {
  switch (remoteData.state) {
    case RemoteData.LOADING:
      return {
        layers: [],
        error: null
      };
    case RemoteData.ERROR:
      return {
        layers: [],
        error: remoteData.value
      };
    case RemoteData.LOADED: {
      const {id, data} = remoteData.value;
      return {
        layers: [
          new PathLayer({
            id: "path-layer",
            data: data.paths,
            getWidth: d => 3,
            getPath: d => d.coordinates,
            getColor: d => d.color
          }),
          new AnimationLayer({
            id: "scatterplot",
            data: data.scatterplot,
            dataId: id,
            time
          })
        ],
        error: null
      };
    }
  }
}

function renderMap({
  mapStyle,
  width,
  height,
  viewState,
  onViewStateChange,
  onLoad,
  onError,
  layers
}) {
  return (
    <DeckGL
      layers={layers}
      views={new MapView({ id: "map" })}
      width={width}
      height={height}
      viewState={viewState}
      controller={MapController}
      onViewStateChange={onViewStateChange}
    >
      <StaticMap
        viewId="map"
        {...viewState}
        width={100}
        height={100}
        reuseMaps
        mapStyle={mapStyle}
        preventStyleDiffing={true}
        mapboxApiAccessToken={REACT_APP_MapboxAccessToken}
        onLoad={onLoad}
        onError={() => console.log("map errored")}
      />
    </DeckGL>
  );
}

export class MapViz extends Component {
  constructor(props) {
    super(props);
    this.state = {
      mapViewState: INITIAL_MAP_VIEW_STATE,
      time: 0,
      buildings: null,
      geometries: [],
      data: RemoteData.loading()
    };
  }
  
  
  componentWillReceiveProps(newProps){
    if(this.props.scenarioCode !== newProps.scenarioCode || this.props.avPercent !== newProps.avPercent){
      const dataId = makeDataId(newProps.scenarioCode, newProps.avPercent);
      loadAttributes(dataId).then(attributes => {
        this.setState({
          data: RemoteData.loaded({
            id: dataId,
            data: zipVizData(this.state.geometries, attributes)
          })
        })
      }).catch(e => {
        this.setState({
          data: RemoteData.error(`Failed to load ${dataId}`)
        });
      });
    }
  }

  onMapLoad = () => {
    const { scenarioCode, avPercent } = this.props;
    // load the first chunk of geometry and the attributes for current scenario and AV level
    const dataId = makeDataId(scenarioCode, avPercent);
    Promise.all([loadGeometry(0), loadAttributes(dataId)])
      .then(([geometries, attributes]) => {
        this.setState(
          {
            data: RemoteData.loaded({
              id: dataId,
              data: zipVizData(geometries, attributes)
            }),
            geometries
          }
        );
        return attributes;
      })
      .catch(e => {
        this.setState({
          data: RemoteData.error(`Failed to load ${dataId}.`)
        });
      })
      .then(attributes => {
        // load the rest of the geometry

        const all = [...Array(GEOMETRY_DATA_CHUNKS - 1).keys()]
          .map(x => x + 1)
          .map(async n => {
            try {
              const geomChunk = await loadGeometry(n);
              const newGeometries = this.state.geometries.concat(geomChunk);
              this.setState({
                geometries: newGeometries,
                data: RemoteData.loaded({
                  id: dataId,
                  data: zipVizData(newGeometries, attributes)
                })
              });
            } catch (e) {
              console.log(e);
              this.setState({
                data: RemoteData.error(`Failed to load geometry ${n}`)
              });
            }
          });

        return Promise.all(all);
      }).then(() => {
        this.animate();
      });
  };

  componentDidMount(){
    this.setState({
      mapViewState: {
          ...INITIAL_MAP_VIEW_STATE,
        zoom: window.innerWidth < 1000? 12.5 : 13.3
      }
    });
  }

  shouldComponentUpdate() {
    return true;
  }

  componentWillUnmount() {
    if (this._animationFrame) {
      window.cancelAnimationFrame(this._animationFrame);
    }
  }

  animate = time => {
    this.setState({
      time: time / 1000 || 0
    });
    this._animationFrame = window.requestAnimationFrame(this.animate);
  };

  onMapViewStateChange = ({ viewState }) => {
    this.setState({
      mapViewState: { ...this.state.mapViewState, ...viewState }
    });
  };

  render() {
    const { data, mapViewState, time } = this.state;
    const { mapStyle, width, height } = this.props;
    const { layers, error } = tryMakeLayers(data, time);

    return (
      <div style={{ width: this.props.width, height: this.props.height, position: "relative"}}>
        {renderMap({
          width,
          height,
          mapStyle,
          viewState: mapViewState,
          onViewStateChange: this.onMapViewStateChange,
          onLoad: this.onMapLoad,
          onError: () => console.log("map errored"),
          layers: layers.concat(ABpointsLayer)
        })}
        {this.props.children}
        {error && renderOverlay(
           renderError(error)
        )}
      </div>
    );
  }
}

MapViz.propTypes = {
  width: PropTypes.string.isRequired,
  height: PropTypes.string.isRequired,
  mapStyle: PropTypes.string.isRequired
};

export { INITIAL_MAP_VIEW_STATE };



// WEBPACK FOOTER //
// ./src/map-viz.js